import React, { useState, useEffect } from 'react';
import config from '../../config/config';

// Add CATEGORY_MAPPING constant at the top of the file
const CATEGORY_MAPPING = {
  'A': 'Ijtimai',
  'B': 'Asaan',
  'C': 'Super Asaan'
};

export function ViewAnimalStatus() {
  const [animals, setAnimals] = useState([]);
  const [filters, setFilters] = useState({
    animal_number: '',
    animal_type: '',
    category: '',
    day: ''
  });
  const [filterStatus, setFilterStatus] = useState('all');

  // Fix useEffect dependency warning by using useCallback
  const fetchAnimals = React.useCallback(async () => {
    try {
      const params = new URLSearchParams({
        ...(filters.animal_number && { animal_number: filters.animal_number }),
        ...(filters.animal_type && { animal_type: filters.animal_type }),
        ...(filters.category && { category: filters.category }),
        ...(filters.day && { day: filters.day }),
        ...(filterStatus !== 'all' && { status: filterStatus }),
        ...(filterStatus === 'delivered' && { include_delivered: 'true' }),
        limit: 100,  // Request 100 records by default
        page: 1      // Request first page by default
      });
  
      const response = await fetch(`${config.baseUrl}/api/animals/animals?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      console.log('API Response:', data); // Verify raw data
      setAnimals(data);
    } catch (error) {
      console.error('Error fetching animals:', error);
    }
  }, [filters, filterStatus]); // Add dependencies here

  // Add refresh interval state
  const [refreshInterval] = useState(10000); // 10 seconds refresh rate

  // Modify useEffect to set up auto-refresh
  useEffect(() => {
    // Initial fetch
    fetchAnimals();

    // Set up auto-refresh interval
    const intervalId = setInterval(() => {
      fetchAnimals();
    }, refreshInterval);

    // Cleanup interval on component unmount
    return () => clearInterval(intervalId);
  }, [fetchAnimals, refreshInterval]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <div className="container mx-auto px-4 py-8 bg-gray-50">
      <h1 className="text-3xl font-bold mb-6 text-gray-800 border-b pb-4">Animal Status Overview</h1>
      
      <div className="mb-6 grid grid-cols-1 md:grid-cols-5 gap-4 bg-white p-6 rounded-lg shadow-sm">
        <input
          type="text"
          placeholder="Search by Animal Number"
          className="p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
          value={filters.animal_number}
          onChange={(e) => handleFilterChange('animal_number', e.target.value)}
        />
        
        <select
          value={filters.animal_type}
          onChange={(e) => handleFilterChange('animal_type', e.target.value)}
          className="p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
        >
          <option value="">Select Animal Type</option>
          <option value="cow">Cow</option>
          <option value="goat">Goat</option>
          <option value="sheep">Sheep</option>
          <option value="camel">Camel</option>
        </select>

        <select
          value={filters.category}
          onChange={(e) => handleFilterChange('category', e.target.value)}
          className="p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
        >
          <option value="">Select Category</option>
          <option value="A">Ijtimai</option>
          <option value="B">Asaan</option>
          <option value="C">Super Asaan</option>
        </select>

        <select
          value={filters.day}
          onChange={(e) => handleFilterChange('day', e.target.value)}
          className="p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
        >
          <option value="">Select Day</option>
          <option value="1">Day 1</option>
          <option value="2">Day 2</option>
          <option value="3">Day 3</option>
        </select>
        
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
        >
          <option value="all">All Statuses</option>
          <option value="slaughtered">Slaughtered</option>
          <option value="in_process">In Process</option>
          <option value="ready_to_dispatch">Ready to Dispatch</option>
          <option value="delivered">Delivered</option>
        </select>
      </div>

      <div className="overflow-x-auto bg-white rounded-lg shadow">
        <table className="w-full table-auto">
          <thead>
            <tr className="bg-gray-100 border-b border-gray-200">
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Animal ID</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Animal Number</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Animal Type</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Category</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Day</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Status</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Last Updated</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {animals
              .filter(animal => 
                (filterStatus === 'all' || animal.status === filterStatus) &&
                (filters.animal_number === '' || animal.animal_number.toLowerCase().includes(filters.animal_number.toLowerCase())) &&
                (filters.animal_type === '' || animal.animal_type === filters.animal_type) &&
                (filters.category === '' || animal.category === filters.category) &&
                (filters.day === '' || animal.day === filters.day)
              )
              .map(animal => (
                <tr 
                  key={`${animal.id}-${animal.animal_type}-${animal.category}`} 
                  className="hover:bg-gray-50 transition-colors"
                >
                  <td className="px-4 py-3 text-sm text-gray-900">{animal.id}</td>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{animal.animal_number}</td>
                  <td className="px-4 py-3 text-sm text-gray-900 capitalize">{animal.animal_type}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">{CATEGORY_MAPPING[animal.category] || animal.category}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">{animal.day}</td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getStatusStyle(animal.status)}`}>
                      {formatStatus(animal.status)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">{new Date(animal.updated_at).toLocaleString()}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Add these helper functions at the top of your file
const getStatusStyle = (status) => {
  const styles = {
    slaughtered: 'bg-green-100 text-green-800',
    in_process: 'bg-yellow-100 text-yellow-800',
    ready_to_dispatch: 'bg-blue-100 text-blue-800',
    delivered: 'bg-gray-100 text-gray-800'
  };
  return styles[status] || 'bg-gray-100 text-gray-800';
};

const formatStatus = (status) => {
  return status.split('_').map(word => 
    word.charAt(0).toUpperCase() + word.slice(1)
  ).join(' ');
};